import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import moment from 'moment';
import { useContext } from 'react';

import { UpdateOccupantDetails } from 'src/api/bookingService';
import { ErrorMessage } from 'src/shared/enum/ErrorMessage';
import { ErrorCode } from 'src/shared/enum/ErrorCode';
import { UserDataFormValues } from 'src/features/details-form/types';

import { mapMonthName } from '../map-month-name-to-number';
import { queryRetry } from '../query-retry';
import { AuthContext } from '../../providers/auth-context';

export const useUpdateDualOccupantDetails = () => {
    const userState = useContext(AuthContext);
    const isLoggedIn = userState.loggedIn;

    return useMutation<void, AxiosError, string>({
        mutationFn: async (vitaRef: string) => {
            if (!isLoggedIn) throw new Error(ErrorMessage.AUTH_ID_ERROR);

            const { user } = userState.session;

            const userId = user.id;

            const userData: UserDataFormValues = user.user_metadata as UserDataFormValues;

            if (
                !userData.dateOfBirth.year ||
                !userData.dateOfBirth.month ||
                !userData.dateOfBirth.day
            )
                throw new Error(ErrorCode.DATE_OF_BIRTH_NOT_FOUND);

            const dob = moment()
                .year(Number(userData.dateOfBirth.year))
                .month(mapMonthName(userData.dateOfBirth.month))
                .day(userData.dateOfBirth.day)
                .toISOString();

            const requestData = {
                id: userId,
                first_name: userData.firstName,
                last_name: userData.lastName,
                address1: userData.address1,
                address2: userData.address2,
                city: userData.city,
                confirmed_student: true,
                country: userData.country,
                nationality: userData.nationality,
                number: userData.phone.callingCode ?? '',
                phone_code: userData.phone.countryCode ?? '',
                post_code: userData.postCode,
                study_year: userData.studyYear,
                university: userData.university,
                dob,
                email: userData.email,
                preferred_language: userData.preferredLanguage,
                is_spanish_resident: userData.isSpanishResident === 'yes',
                nie_number: userData.nieNumber ?? null,
                nif_number: userData.nifNumber ?? null,
                passport_number: userData.passportNumber ?? null,
                passport_issuing_country: userData.passportIssuingCountry ?? null,
            };

            await UpdateOccupantDetails({
                vitaRef,
                occupant: requestData,
            });
        },
        retry: queryRetry,
    });
};
